/**
 * DCSE · <tabbed-carousel>
 *
 * Real WAI-ARIA tablist/tab/tabpanel behavior for sections/ADD-carrusel-categorias.liquid:
 * clicking (or arrow/Home/End-keying through) a category tab shows the matching product
 * panel, no page reload, roving tabindex, and the active tab is written to the URL
 * (?categoria=handle) via history.replaceState so it's shareable/refreshable.
 *
 * Moved out of the section's inline <script> into its own deferred asset (same pattern as
 * dcse-quick-add.js) so it's cacheable separately from the section's HTML. Guarded with
 * customElements.get so it's safe if the section renders more than once on the same page.
 */
(function () {
  if (customElements.get('tabbed-carousel')) return;

  class TabbedCarousel extends HTMLElement {
    constructor() {
      super();
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    }

    connectedCallback() {
      if (!this.tabs.length || !this.panels.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick.bind(this));
        tab.addEventListener('keydown', this.onKeyDown.bind(this));
      });

      this.onPopState = this.syncFromUrl.bind(this);
      window.addEventListener('popstate', this.onPopState);

      this.syncFromUrl();
    }

    disconnectedCallback() {
      if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
    }

    get queryParam() {
      return this.dataset.queryParam || 'categoria';
    }

    syncFromUrl() {
      var value = new URLSearchParams(window.location.search).get(this.queryParam);
      if (!value) return;
      var match = this.tabs.find(function (tab) {
        return tab.dataset.category === value;
      });
      if (match) this.activateTab(match, { focus: false, updateUrl: false });
    }

    onTabClick(event) {
      this.activateTab(event.currentTarget, { focus: false, updateUrl: true });
    }

    onKeyDown(event) {
      var currentIndex = this.tabs.indexOf(event.currentTarget);
      var newIndex = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          newIndex = (currentIndex + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activateTab(this.tabs[newIndex], { focus: true, updateUrl: true });
    }

    activateTab(tab, options) {
      options = options || {};
      if (!tab) return;

      if (tab.getAttribute('aria-selected') !== 'true') {
        this.tabs.forEach((t) => {
          var isActive = t === tab;
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          t.tabIndex = isActive ? 0 : -1;
          t.classList.toggle('tabbed-carousel__tab--active', isActive);
        });

        var targetId = tab.getAttribute('aria-controls');
        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== targetId;
        });

        if (options.updateUrl && window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set(this.queryParam, tab.dataset.category);
          window.history.replaceState(window.history.state, '', url);
        }
      }

      if (options.focus) tab.focus();
    }
  }

  customElements.define('tabbed-carousel', TabbedCarousel);
})();
